import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { format, addDays } from 'date-fns'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import type { Profile, Site } from '@/types/database'
import { PLAN_LIMITS } from '@/lib/config'

export default function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [user, setUser] = useState<Profile | null>(null)
  const [sites, setSites] = useState<Site[]>([])
  const [eventsOverTime, setEventsOverTime] = useState<{ date: string; count: number }[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (id) {
      fetchUser()
    }
  }, [id])

  const fetchUser = async () => {
    if (!id) return
    setIsLoading(true)

    try {
      const { data: userData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', id)
        .single() as { data: Profile | null }

      if (userData) {
        setUser(userData)

        // Fetch user's sites
        const { data: sitesData } = await supabase
          .from('sites')
          .select('*')
          .eq('user_id', id) as { data: Site[] | null }

        setSites(sitesData || [])

        // Fetch events over time for this user's sites
        if (sitesData && sitesData.length > 0) {
          const siteIds = sitesData.map(s => s.id)
          const { data: dailyStats } = await supabase
            .from('daily_stats')
            .select('date, total_events')
            .in('site_id', siteIds)
            .order('date', { ascending: true })
            .limit(30) as { data: { date: string; total_events: number }[] | null }

          const eventsByDay = dailyStats?.reduce((acc, d) => {
            acc[d.date] = (acc[d.date] || 0) + d.total_events
            return acc
          }, {} as Record<string, number>) || {}

          setEventsOverTime(
            Object.entries(eventsByDay).map(([date, count]) => ({ date, count }))
          )
        }
      }
    } catch (error) {
      console.error('Failed to fetch user:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const updateUser = async (updates: Partial<Profile>) => {
    if (!id) return
    setIsSaving(true)

    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates as never)
        .eq('id', id)

      if (error) throw error

      setUser(prev => prev ? { ...prev, ...updates } : null)
    } catch (error) {
      console.error('Failed to update user:', error)
      alert('Failed to update user')
    } finally {
      setIsSaving(false)
    }
  }

  const deleteUser = async () => {
    if (!id || !confirm('Are you sure you want to delete this user? This cannot be undone.')) {
      return
    }

    try {
      // Delete profile (cascades to sites, sessions, events)
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', id)

      if (error) throw error

      navigate('/admin/users')
    } catch (error) {
      console.error('Failed to delete user:', error)
      alert('Failed to delete user')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">User not found</p>
        <Link to="/admin/users" className="text-primary-400 mt-4 inline-block">
          Back to users
        </Link>
      </div>
    )
  }

  return (
    <div>
      <Link to="/admin/users" className="text-gray-400 hover:text-gray-300 mb-4 inline-block">
        ← Back to users
      </Link>

      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">{user.email}</h1>
          <p className="text-gray-400">
            Member since {format(new Date(user.created_at), 'MMMM d, yyyy')}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => updateUser({ is_disabled: !user.is_disabled })}
            disabled={isSaving}
            className={user.is_disabled ? 'btn-primary' : 'btn-secondary bg-gray-700 border-gray-600 text-gray-300'}
          >
            {user.is_disabled ? 'Enable Account' : 'Disable Account'}
          </button>
          <button onClick={deleteUser} className="btn-danger">
            Delete User
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* User info */}
        <div className="space-y-6">
          <div className="bg-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Account Info</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-400">Email</dt>
                <dd className="text-white">{user.email}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-400">Role</dt>
                <dd>
                  <select
                    value={user.role}
                    onChange={(e) => updateUser({ role: e.target.value as 'user' | 'admin' })}
                    disabled={isSaving}
                    className="bg-gray-700 border-gray-600 text-white rounded px-2 py-1 text-sm"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-400">Status</dt>
                <dd>
                  {user.is_disabled ? (
                    <span className="badge-danger">Disabled</span>
                  ) : (
                    <span className="badge-success">Active</span>
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-400">Stripe ID</dt>
                <dd className="text-white font-mono text-xs">
                  {user.stripe_customer_id || '-'}
                </dd>
              </div>
            </dl>
          </div>

          <div className="bg-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Subscription</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-400">Status</dt>
                <dd>
                  <select
                    value={user.subscription_status}
                    onChange={(e) => updateUser({ subscription_status: e.target.value as Profile['subscription_status'] })}
                    disabled={isSaving}
                    className="bg-gray-700 border-gray-600 text-white rounded px-2 py-1 text-sm"
                  >
                    <option value="trialing">Trialing</option>
                    <option value="active">Active</option>
                    <option value="canceled">Canceled</option>
                    <option value="past_due">Past Due</option>
                  </select>
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-400">Trial Ends</dt>
                <dd className="text-white">
                  {user.trial_ends_at
                    ? format(new Date(user.trial_ends_at), 'MMM d, yyyy')
                    : '-'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-400">Period End</dt>
                <dd className="text-white">
                  {user.current_period_end
                    ? format(new Date(user.current_period_end), 'MMM d, yyyy')
                    : '-'}
                </dd>
              </div>
            </dl>

            <div className="mt-4 pt-4 border-t border-gray-700">
              <p className="text-sm text-gray-400 mb-2">Extend Trial</p>
              <div className="flex gap-2">
                {[7, 14, 30].map((days) => (
                  <button
                    key={days}
                    onClick={() => updateUser({
                      trial_ends_at: addDays(new Date(), days).toISOString(),
                      subscription_status: 'trialing',
                    })}
                    disabled={isSaving}
                    className="px-3 py-1 text-sm bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
                  >
                    +{days}d
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Usage This Month</h3>
            <div className="space-y-4">
              <UsageBar
                label="Sessions"
                value={user.sessions_count}
                max={PLAN_LIMITS.sessionsPerMonth}
              />
              <UsageBar
                label="Events"
                value={user.events_count}
                max={PLAN_LIMITS.eventsPerMonth}
              />
            </div>
          </div>
        </div>

        {/* Charts and sites */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Events Over Time</h3>
            <div className="h-64">
              {eventsOverTime.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={eventsOverTime}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(date) => format(new Date(date), 'MMM d')}
                      stroke="#9ca3af"
                      fontSize={12}
                    />
                    <YAxis stroke="#9ca3af" fontSize={12} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1f2937', border: 'none' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Line type="monotone" dataKey="count" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400">
                  No event data available
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Sites</h3>
            {sites.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-700">
                    <th className="pb-3 font-medium">Domain</th>
                    <th className="pb-3 font-medium">Name</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {sites.map((site) => (
                    <tr key={site.id} className="border-b border-gray-700 last:border-0">
                      <td className="py-3 text-white">{site.domain}</td>
                      <td className="py-3 text-gray-300">{site.name}</td>
                      <td className="py-3">
                        {site.is_active ? (
                          <span className="badge-success">Active</span>
                        ) : (
                          <span className="badge-gray">Inactive</span>
                        )}
                      </td>
                      <td className="py-3 text-gray-400">
                        {format(new Date(site.created_at), 'MMM d, yyyy')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-gray-400">No sites configured</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function UsageBar({ label, value, max }: { label: string; value: number; max: number }) {
  const percentage = Math.min((value / max) * 100, 100)
  const isWarning = percentage > 80
  const isDanger = percentage > 95

  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-400">{label}</span>
        <span className={isDanger ? 'text-red-400' : isWarning ? 'text-yellow-400' : 'text-gray-300'}>
          {value.toLocaleString()} / {max.toLocaleString()}
        </span>
      </div>
      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            isDanger ? 'bg-red-500' : isWarning ? 'bg-yellow-500' : 'bg-primary-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  )
}
